import { Injectable } from '@angular/core';
import { ExpensesService } from '../expenses.service';

export interface BoardingPassNotify {
    success(message: string, title?: string): void;
    error(message: string, title?: string): void;
}

@Injectable({
    providedIn: 'root'
})
export class BoardingPassService {
    constructor(private expensesService: ExpensesService) { }

    getUrl(invoice: any): string {
        return invoice?.boardingPassURL || invoice?.boardingPassUrl || invoice?.boardingpassURL || '';
    }

    isEligible(trip: any): boolean {
        return ['1'].includes(String(trip?.productId));
    }

    isBoardingPassFeatureEnabled(): boolean {
        try {
            const attributes = JSON.parse(localStorage.getItem('companyCustomAttributes') || '[]');
            const boardingPassConfig = (attributes || []).find((item: any) =>
                item?.attributeName === 'SHOW_BOARDING_PASS_UPLOAD'
            );
            return String(boardingPassConfig?.attributeValue) === '1';
        } catch {
            return false;
        }
    }

    canShowBoardingPassSection(claimId: any, claimStatus?: any): boolean {
        if (!this.isBoardingPassFeatureEnabled()) {
            return false;
        }
        // Unsaved Create Claim has no claimId/status yet; upload still uses invoiceId.
        if (claimId == null || claimId === '') {
            return true;
        }
        const status = Number(claimStatus);
        return status === 1 || status === 11;
    }

    getUploadedInvoices(trip: any): any[] {
        if (!this.isEligible(trip)) {
            return [];
        }
        const invoices = trip?.invoiceList?.length > 0 ? trip.invoiceList : (trip?.tripServiceAttachments || []);
        return (invoices || []).filter((invoice: any) => this.isUploaded(invoice));
    }

    getDisplayInvoices(trip: any, claimId: any, claimStatus?: any): any[] {
        if (!this.isBoardingPassFeatureEnabled()) {
            return [];
        }
        if (this.canShowBoardingPassSection(claimId, claimStatus)) {
            return this.getInvoices(trip);
        }
        return this.getUploadedInvoices(trip);
    }

    getDisplayUploadedCount(trip: any, claimId: any, claimStatus?: any): number {
        return this.getDisplayInvoices(trip, claimId, claimStatus).filter((invoice: any) => this.isUploaded(invoice)).length;
    }

    getDisplayTotalCount(trip: any, claimId: any, claimStatus?: any): number {
        return this.getDisplayInvoices(trip, claimId, claimStatus).length;
    }

    getDisplayCountLabel(trip: any, claimId: any, claimStatus?: any): string {
        const uploaded = this.getDisplayUploadedCount(trip, claimId, claimStatus);
        if (this.canShowBoardingPassSection(claimId, claimStatus)) {
            const total = this.getDisplayTotalCount(trip, claimId, claimStatus);
            return `${uploaded}/${total} uploaded`;
        }
        return uploaded === 1 ? '1 uploaded' : `${uploaded} uploaded`;
    }

    isRequired(invoice: any): boolean {
        return invoice?.boardingPassRequired === true || invoice?.boardingPassRequired === 'true';
    }

    isUploaded(invoice: any): boolean {
        return !!this.getUrl(invoice);
    }

    getSector(invoice: any): string {
        const departure = invoice?.departure || 'From';
        const arrival = invoice?.arrival || 'To';
        return `${departure} - ${arrival}`;
    }

    getFileName(invoice: any): string {
        if (invoice?.boardingPassFileName) {
            return invoice.boardingPassFileName;
        }
        const url = this.getUrl(invoice);
        if (url) {
            const urlPath = url.split('?')[0];
            const fileName = urlPath.substring(urlPath.lastIndexOf('/') + 1);
            if (fileName) {
                return fileName;
            }
        }
        return invoice?.fileName || 'Boarding pass';
    }

    getInvoices(trip: any): any[] {
        if (!this.isBoardingPassFeatureEnabled() || !this.isEligible(trip)) {
            return [];
        }
        const invoices = trip?.invoiceList?.length > 0 ? trip.invoiceList : (trip?.tripServiceAttachments || []);
        return (invoices || []).filter((invoice: any) => this.isRequired(invoice));
    }

    getTotal(trip: any): number {
        return this.getInvoices(trip).length;
    }

    getUploadedCount(trip: any): number {
        return this.getInvoices(trip).filter((invoice: any) => this.isUploaded(invoice)).length;
    }

    getChipLabel(trip: any): string {
        const total = this.getTotal(trip);
        const uploaded = this.getUploadedCount(trip);
        return uploaded == total && total > 0 ? 'Done' : `${uploaded}/${total}`;
    }

    normalizeInvoice(invoice: any): any {
        if (!invoice) {
            return invoice;
        }
        const boardingPassURL = this.getUrl(invoice);
        const normalized = { ...invoice };
        delete normalized.boardingPassUrl;
        delete normalized.boardingpassURL;
        if (boardingPassURL) {
            normalized.boardingPassURL = boardingPassURL;
        } else {
            delete normalized.boardingPassURL;
        }
        return normalized;
    }

    sanitizeInvoiceForPayload(invoice: any): any {
        if (!invoice) {
            return invoice;
        }
        const sanitized = this.normalizeInvoice(invoice);
        delete sanitized.boardingPassFileName;
        delete sanitized.boardingPassMissing;
        delete sanitized.boardingPassUploading;
        return sanitized;
    }

    sanitizeTripListForSave(trips: any[]): any[] {
        return (trips || []).map((trip: any) => ({
            ...trip,
            invoiceList: (trip?.invoiceList || []).map((invoice: any) => this.sanitizeInvoiceForPayload(invoice)),
            tripServiceAttachments: (trip?.tripServiceAttachments || []).map((invoice: any) => this.sanitizeInvoiceForPayload(invoice))
        }));
    }

    isSameInvoice(left: any, right: any): boolean {
        if (!left || !right) {
            return false;
        }
        if (left.invoiceId != null && left.invoiceId !== '' && right.invoiceId != null && right.invoiceId !== '') {
            return left.invoiceId == right.invoiceId;
        }
        if (left.requestOptionId != null && left.requestOptionId !== '' && right.requestOptionId != null && right.requestOptionId !== '') {
            return left.requestOptionId == right.requestOptionId;
        }
        return false;
    }

    mergeInvoiceLists(primaryList: any[], secondaryList: any[]): any[] {
        return (primaryList || []).map((primaryInv: any) => {
            const secondaryInv = (secondaryList || []).find((item: any) => this.isSameInvoice(item, primaryInv));
            const boardingPassURL = this.getUrl(primaryInv) || this.getUrl(secondaryInv);
            return this.normalizeInvoice({
                ...secondaryInv,
                ...primaryInv,
                boardingPassURL,
                boardingPassRequired: primaryInv?.boardingPassRequired ?? secondaryInv?.boardingPassRequired,
                departure: primaryInv?.departure || secondaryInv?.departure,
                arrival: primaryInv?.arrival || secondaryInv?.arrival
            });
        });
    }

    /**
     * TEMPORARY WORKAROUND for incorrect boarding pass flags in update-service-list response.
     * Remove this method once v1/trip-details/update-service-list returns correct boarding pass data.
     */
    private omitBoardingPassFieldsFromUpdateServiceList(trips: any[]): any[] {
        const boardingPassInvoiceKeys = [
            'boardingPassRequired',
            'boardingPassURL',
            'boardingPassUrl',
            'boardingpassURL',
            'preSignedBoardingPassURL'
        ];

        const omitInvoiceBoardingPassFields = (invoice: any) => {
            if (!invoice) {
                return invoice;
            }
            const cleanedInvoice = { ...invoice };
            boardingPassInvoiceKeys.forEach((key) => delete cleanedInvoice[key]);
            return cleanedInvoice;
        };

        return (trips || []).map((trip: any) => {
            const cleanedTrip = { ...trip };
            delete cleanedTrip.boardingpassList;

            if (cleanedTrip.invoiceList?.length) {
                cleanedTrip.invoiceList = cleanedTrip.invoiceList.map((invoice: any) => omitInvoiceBoardingPassFields(invoice));
            }
            if (cleanedTrip.tripServiceAttachments?.length) {
                cleanedTrip.tripServiceAttachments = cleanedTrip.tripServiceAttachments.map((invoice: any) => omitInvoiceBoardingPassFields(invoice));
            }
            if (cleanedTrip.ticketList?.length) {
                cleanedTrip.ticketList = cleanedTrip.ticketList.map((invoice: any) => omitInvoiceBoardingPassFields(invoice));
            }
            return cleanedTrip;
        });
    }

    normalizeTripList(trips: any[]): any[] {
        return (trips || []).map((trip: any) => {
            const invoiceList = trip?.invoiceList?.length > 0 ? trip.invoiceList : (trip?.tripServiceAttachments || []);
            const tripServiceAttachments = trip?.tripServiceAttachments?.length > 0 ? trip.tripServiceAttachments : invoiceList;
            const normalizedInvoiceList = this.mergeInvoiceLists(invoiceList, tripServiceAttachments);
            const normalizedAttachments = this.mergeInvoiceLists(tripServiceAttachments, invoiceList);
            const tripAmount = trip?.totalAmount || trip?.tripCost;
            const formattedAmount = trip?.formattedTotalAmount || trip?.formattedTripCost ||
                (tripAmount ? Math.round(Number(tripAmount?.toString().replace(/,/g, '') || 0)).toLocaleString('en-IN') : trip?.formattedTotalAmount);
            return {
                ...trip,
                fromLocation: trip?.fromLocation || trip?.tripName,
                startDate: trip?.startDate || trip?.tripStartDate,
                endDate: trip?.endDate || trip?.tripEndDate,
                totalAmount: tripAmount,
                formattedTotalAmount: formattedAmount,
                tripName: trip?.tripName || trip?.fromLocation,
                tripStartDate: trip?.tripStartDate || trip?.startDate,
                tripEndDate: trip?.tripEndDate || trip?.endDate,
                invoiceList: normalizedInvoiceList,
                tripServiceAttachments: normalizedAttachments
            };
        });
    }

    mergeTripList(sourceTrips: any[], updatedTrips: any[], ignoreBoardingPassFromUpdated = false): any[] {
        // TEMPORARY WORKAROUND (START)
        // update-service-list currently returns incorrect boardingPassRequired/boardingPassURL values.
        // Until the API is fixed, keep boarding pass state from report-summary / report-get (sourceTrips).
        // Remove ignoreBoardingPassFromUpdated usage and omitBoardingPassFieldsFromUpdateServiceList once API is corrected.
        const tripsToMerge = ignoreBoardingPassFromUpdated
            ? this.omitBoardingPassFieldsFromUpdateServiceList(updatedTrips)
            : updatedTrips;
        // TEMPORARY WORKAROUND (END)

        return this.normalizeTripList((tripsToMerge || []).map((updatedTrip: any, index: number) => {
            const sourceTrip = (sourceTrips || []).find((trip: any) =>
                (updatedTrip?.tripGUID && trip?.tripGUID === updatedTrip?.tripGUID) ||
                (updatedTrip?.tripId && trip?.tripId == updatedTrip?.tripId)
            ) || sourceTrips?.[index];
            if (!sourceTrip) {
                return updatedTrip;
            }

            const updatedInvoiceList = updatedTrip?.invoiceList?.length > 0 ? updatedTrip.invoiceList : (updatedTrip?.tripServiceAttachments || []);
            const sourceInvoiceList = sourceTrip?.invoiceList?.length > 0 ? sourceTrip.invoiceList : (sourceTrip?.tripServiceAttachments || []);
            const updatedAttachments = updatedTrip?.tripServiceAttachments?.length > 0 ? updatedTrip.tripServiceAttachments : updatedInvoiceList;
            const sourceAttachments = sourceTrip?.tripServiceAttachments?.length > 0 ? sourceTrip.tripServiceAttachments : sourceInvoiceList;

            return {
                ...updatedTrip,
                invoiceList: this.mergeInvoiceLists(updatedInvoiceList, sourceInvoiceList),
                tripServiceAttachments: this.mergeInvoiceLists(updatedAttachments, sourceAttachments)
            };
        }));
    }

    syncInvoice(trip: any, invoice: any): void {
        const match = (item: any) => this.isSameInvoice(item, invoice);

        trip.invoiceList = trip.invoiceList || [];
        trip.tripServiceAttachments = trip?.tripServiceAttachments?.length > 0 ? trip.tripServiceAttachments : trip.invoiceList;

        const invoiceIndex = trip.invoiceList.findIndex(match);
        if (invoiceIndex >= 0) {
            trip.invoiceList[invoiceIndex] = invoice;
        }

        const attachmentIndex = trip.tripServiceAttachments.findIndex(match);
        if (attachmentIndex >= 0) {
            trip.tripServiceAttachments[attachmentIndex] = invoice;
        }
    }

    applyUploadedInvoice(invoice: any, url: string, fileName: string): void {
        invoice.boardingPassURL = url;
        invoice.boardingPassFileName = fileName;
        delete invoice.boardingPassUrl;
        delete invoice.boardingpassURL;
        invoice.boardingPassMissing = false;
    }

    clearUploadedInvoice(invoice: any): void {
        invoice.boardingPassURL = '';
        invoice.boardingPassFileName = '';
        delete invoice.boardingPassUrl;
        delete invoice.boardingpassURL;
    }

    getMissingRequiredMessage(trips: any[]): string {
        const missingSectors = [];
        (trips || []).forEach((trip: any) => {
            this.getInvoices(trip).forEach((invoice: any) => {
                const isMissing = this.isRequired(invoice) && !this.isUploaded(invoice);
                invoice.boardingPassMissing = isMissing;
                if (isMissing) {
                    missingSectors.push(this.getSector(invoice));
                }
            });
        });

        if (!missingSectors.length) {
            return '';
        }

        const missingSectorText = missingSectors
            .map((sector: string) => sector.replace(/\s+-\s+/g, ' to '))
            .join(', ');
        return `Please upload boarding passes for ${missingSectorText}.`;
    }

    hasMissingRequired(trips: any[]): boolean {
        return !!this.getMissingRequiredMessage(trips);
    }

    upload(event: any, trip: any, invoice: any, userInfo: any, notify: BoardingPassNotify, onSuccess?: () => void): void {
        const file = event?.target?.files?.[0];
        if (!file || !trip || !invoice) {
            return;
        }

        if (!invoice?.invoiceId) {
            notify.error('Invoice details missing. Unable to upload boarding pass', 'Error');
            event.target.value = '';
            return;
        }

        const extension = file.name?.split('?')[0]?.split('.').pop()?.toLowerCase();
        const allowedExtensions = userInfo?.attachmentFormat ? userInfo.attachmentFormat.split(',').map((item: string) => item.trim().toLowerCase()) : [];
        if (allowedExtensions?.length > 0 && !allowedExtensions.includes(extension)) {
            notify.error('invalid extension. Extension Should be ' + allowedExtensions.join(', ') + '', 'Error');
            event.target.value = '';
            return;
        }

        invoice.boardingPassUploading = true;
        const requestBody = {
            legalEntityGUID: userInfo.legalEntityGuId,
            legalEntityId: userInfo.legalEntityId,
            fileName: file.name,
            uploadFor: 'EXPENSE-ATTACHMMENTS'
        };

        this.expensesService.getUploadUrl(requestBody).subscribe((res: any) => {
            sessionStorage.setItem('s3uploadUrl', res.payload?.s3Url);
            this.expensesService.uploadFileInS3(file, res.payload?.preSignedS3URL).subscribe((response: any) => {
                if (response?.status == 200) {
                    const boardingPassPayload = [{
                        invoiceId: invoice.invoiceId,
                        boardingpassURL: res.payload.s3Url
                    }];
                    this.expensesService.uploadBoardingPass(boardingPassPayload).subscribe((apiRes: any) => {
                        if (!apiRes?.isError && apiRes?.errorCode === 0) {
                            this.applyUploadedInvoice(invoice, res.payload.s3Url, file.name);
                            this.syncInvoice(trip, invoice);
                            notify.success('Boarding pass uploaded successfully', 'Success');
                            onSuccess?.();
                        } else {
                            notify.error(apiRes?.errorMessage || 'Unable to save boarding pass', 'Error');
                        }
                        invoice.boardingPassUploading = false;
                        event.target.value = '';
                        sessionStorage.removeItem('s3uploadUrl');
                    }, () => {
                        invoice.boardingPassUploading = false;
                        event.target.value = '';
                        sessionStorage.removeItem('s3uploadUrl');
                        notify.error('Unable to save boarding pass', 'Error');
                    });
                } else {
                    invoice.boardingPassUploading = false;
                    event.target.value = '';
                    sessionStorage.removeItem('s3uploadUrl');
                    notify.error('Unable to upload boarding pass');
                }
            }, () => {
                invoice.boardingPassUploading = false;
                event.target.value = '';
                sessionStorage.removeItem('s3uploadUrl');
                notify.error('Unable to upload boarding pass');
            });
        }, () => {
            invoice.boardingPassUploading = false;
            event.target.value = '';
            sessionStorage.removeItem('s3uploadUrl');
            notify.error('Unable to upload boarding pass');
        });
    }

    remove(trip: any, invoice: any, notify: BoardingPassNotify, onSuccess?: () => void): void {
        if (!invoice?.invoiceId) {
            notify.error('Invoice details missing. Unable to delete boarding pass', 'Error');
            return;
        }

        invoice.boardingPassUploading = true;
        this.expensesService.deleteBoardingPass([{ invoiceId: invoice.invoiceId }]).subscribe((apiRes: any) => {
            if (!apiRes?.isError && apiRes?.errorCode === 0) {
                this.clearUploadedInvoice(invoice);
                this.syncInvoice(trip, invoice);
                notify.success('Boarding pass removed successfully', 'Success');
                onSuccess?.();
            } else {
                notify.error(apiRes?.errorMessage || 'Unable to delete boarding pass', 'Error');
            }
            invoice.boardingPassUploading = false;
        }, () => {
            invoice.boardingPassUploading = false;
            notify.error('Unable to delete boarding pass', 'Error');
        });
    }
}
